// widget/src/components/BasketDockChip.tsx
//
// Phase 2 mini-cart — the peek-state content for BottomSheet on mobile.
// Renders a tappable chip showing basket count + subtotal: "🛒 3 · $1,572".
// When the basket is empty we show a faded placeholder so the chip still
// occupies its slot and serves as an affordance to expand the sheet.
//
// 48px min-height keeps the tap target large enough for thumb use even with
// the parent's 64px peek-state height (the chip is centered inside it).

import type { PendingBasket } from "../types"
import styles from "../styles/widgetFin.module.css"

function fmtTotal(cents: number): string {
  // Whole-dollar rounding for the dock chip — saves horizontal space
  // (cart screens still show cents). Use toLocaleString so 1572 → "1,572".
  return `$${Math.round(cents / 100).toLocaleString()}`
}

// Minimalist basket-bag SVG. ~16px nominal — scales via flex sizing.
const BasketIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.9"
    strokeLinecap="round"
    strokeLinejoin="round"
    width="18"
    height="18"
    aria-hidden="true"
  >
    <path d="M3 8h18l-1.5 11.5A2 2 0 0 1 17.5 21h-11a2 2 0 0 1-2-1.5L3 8z" />
    <path d="M8 8V6a4 4 0 0 1 8 0v2" />
  </svg>
)

export function BasketDockChip({
  basket,
  onClick,
}: {
  basket: PendingBasket | null
  onClick?: () => void
}) {
  const count = basket?.items.reduce((sum, it) => sum + it.quantity, 0) ?? 0
  const empty = count === 0

  return (
    <button
      type="button"
      className={styles["basketDockChip"]}
      data-empty={empty || undefined}
      onClick={onClick}
      aria-label={
        empty
          ? "Open cart — empty"
          : `Open cart — ${count} item${count === 1 ? "" : "s"}, ${fmtTotal(
              basket!.subtotal,
            )}`
      }
    >
      <span className={styles["basketDockChipIcon"]}>
        <BasketIcon />
      </span>
      {empty ? (
        <span className={styles["basketDockChipPlaceholder"]}>
          Ask Pearl to start shopping
        </span>
      ) : (
        <span className={styles["basketDockChipText"]}>
          <span className={styles["basketDockChipCount"]}>
            {count} item{count === 1 ? "" : "s"}
          </span>
          <span className={styles["basketDockChipDot"]} aria-hidden="true">
            ·
          </span>
          <span className={styles["basketDockChipTotal"]}>
            {fmtTotal(basket!.subtotal)}
          </span>
        </span>
      )}
    </button>
  )
}
